/**
 * Money formatting for values stored in FULL rupees (e.g. limit info), with a
 * unit toggle: lakhs / crores / full number.
 */
export const MONEY_UNIT_OPTIONS = [
    { key: "lacs", label: "In Lacs" },
    { key: "cr", label: "In Cr" },
    { key: "full", label: "In Full" }
];

export function moneyUnitLabel(unit) {
    const found = MONEY_UNIT_OPTIONS.find(u => u.key === unit);
    return found ? found.label : "In Cr";
}

// Always-lakhs rupee format WITHOUT the ₹ symbol, for cards whose rows must be
// comparable at a glance (e.g. GST Sales YTD vs FY vs FY): letting each row pick
// its own unit renders "98.18 Lakh" next to "3.02 Cr", which reads as the larger
// number being smaller. Returns null for empty/invalid input.
export function formatLakhAmount(valueInRupees) {
    if (
        valueInRupees === null ||
        valueInRupees === undefined ||
        valueInRupees === ""
    ) {
        return null;
    }
    const n = Number(String(valueInRupees).replace(/[^0-9.-]/g, ""));
    if (!isFinite(n) || Number.isNaN(n)) {
        return null;
    }
    const v = Math.round((n / 100000) * 100) / 100;
    return v.toLocaleString("en-IN", { maximumFractionDigits: 2 }) + " Lakh";
}

// Compact rupee format WITHOUT the ₹ symbol (the caller renders ₹ separately),
// mirroring the Credit Brief PDF's `_cr`: >=1 Cr -> "X Cr", >=1 Lakh -> "X Lakh",
// else the full Indian-grouped integer. Returns null for empty/invalid input.
export function formatCompactAmount(valueInRupees) {
    if (
        valueInRupees === null ||
        valueInRupees === undefined ||
        valueInRupees === ""
    ) {
        return null;
    }
    const n = Number(String(valueInRupees).replace(/[^0-9.-]/g, ""));
    if (!isFinite(n) || Number.isNaN(n)) {
        return null;
    }
    const abs = Math.abs(n);
    if (abs >= 10000000) {
        const v = Math.round((n / 10000000) * 100) / 100;
        return v.toLocaleString("en-IN", { maximumFractionDigits: 2 }) + " Cr";
    }
    if (abs >= 100000) {
        const v = Math.round((n / 100000) * 100) / 100;
        return (
            v.toLocaleString("en-IN", { maximumFractionDigits: 2 }) + " Lakh"
        );
    }
    return Math.round(n).toLocaleString("en-IN");
}

// Formats a full-rupee value into the chosen unit (₹ + grouped + suffix).
export function formatMoney(valueInRupees, unit) {
    if (
        valueInRupees === null ||
        valueInRupees === undefined ||
        valueInRupees === ""
    ) {
        return null;
    }
    const n = Number(String(valueInRupees).replace(/[^0-9.-]/g, ""));
    if (!isFinite(n) || Number.isNaN(n)) {
        return String(valueInRupees);
    }
    if (unit === "lacs") {
        const v = Math.round((n / 100000) * 100) / 100;
        return (
            "₹" + v.toLocaleString("en-IN", { maximumFractionDigits: 2 }) + " L"
        );
    }
    if (unit === "full") {
        return "₹" + Math.round(n).toLocaleString("en-IN");
    }
    // default: crores
    const v = Math.round((n / 10000000) * 100) / 100;
    return "₹" + v.toLocaleString("en-IN", { maximumFractionDigits: 2 }) + " Cr";
}
